import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, Save, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import GlassCard from "@/components/GlassCard";
import CompliancePolicyLibrary from "@/components/CompliancePolicyLibrary";

/**
 * ContentRestrictionsSection
 * --------------------------
 * Optional, off-by-default settings block that lets a business owner
 * supply their own content restrictions and disclosure preferences.
 *
 * IMPORTANT — this is a prompt-shaping preference only:
 *   • Postmoro layers these instructions into AI generation prompts.
 *   • Postmoro does NOT claim that content is compliant, approved,
 *     legally reviewed, or guaranteed to satisfy any regulation.
 *   • Publishing, scheduling, and Autopilot approval behavior are
 *     UNCHANGED. The amber banner only recommends manual review and
 *     links to the existing Autopilot approval setting.
 *
 * Storage: nested `content_restrictions` object on the existing business
 * profile document. Persisted via the existing PUT /api/profile endpoint.
 * No new database collection. No schema migration.
 */

const MAX_LEN = 3000;

const emptyState = {
  enabled: false,
  industry_or_rules: "",
  prohibited_claims: "",
  required_disclosures: "",
  additional_restrictions: "",
  acknowledged: false,
};

function coerceIncoming(cr) {
  if (!cr || typeof cr !== "object") return { ...emptyState };
  return {
    enabled: Boolean(cr.enabled),
    industry_or_rules: typeof cr.industry_or_rules === "string" ? cr.industry_or_rules : "",
    prohibited_claims: typeof cr.prohibited_claims === "string" ? cr.prohibited_claims : "",
    required_disclosures: typeof cr.required_disclosures === "string" ? cr.required_disclosures : "",
    additional_restrictions: typeof cr.additional_restrictions === "string" ? cr.additional_restrictions : "",
    acknowledged: Boolean(cr.acknowledged),
  };
}

export const ContentRestrictionsSection = ({ initial, onSaved }) => {
  const navigate = useNavigate();
  const [state, setState] = useState(() => coerceIncoming(initial));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState(() => coerceIncoming(initial));

  useEffect(() => {
    const coerced = coerceIncoming(initial);
    setState(coerced);
    setInitialSnapshot(coerced);
    setDirty(false);
  }, [initial]);

  const set = (patch) => {
    setState((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const canEnable = state.acknowledged;
  const bannerVisible = state.enabled && state.acknowledged;

  const truncate = (v) => (typeof v === "string" && v.length > MAX_LEN ? v.slice(0, MAX_LEN) : v);

  const characterCount = (v) => (typeof v === "string" ? v.length : 0);

  const savedPayload = useMemo(
    () => ({
      enabled: Boolean(state.enabled && state.acknowledged),
      industry_or_rules: truncate(state.industry_or_rules || ""),
      prohibited_claims: truncate(state.prohibited_claims || ""),
      required_disclosures: truncate(state.required_disclosures || ""),
      additional_restrictions: truncate(state.additional_restrictions || ""),
      acknowledged: Boolean(state.acknowledged),
    }),
    [state],
  );

  const onSave = async () => {
    // Client-side gate mirrors the server rule: enabling requires ack.
    if (state.enabled && !state.acknowledged) {
      toast.error(
        "Please acknowledge the review responsibility statement before enabling content restrictions.",
      );
      return;
    }
    setSaving(true);
    try {
      const res = await api.put("/profile", { content_restrictions: savedPayload });
      const fresh = coerceIncoming(res?.data?.content_restrictions);
      setState(fresh);
      setInitialSnapshot(fresh);
      setDirty(false);
      toast.success("Content restrictions saved");
      if (typeof onSaved === "function") onSaved(res?.data || null);
    } catch (err) {
      toast.error("Could not save content restrictions");
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    setState(initialSnapshot);
    setDirty(false);
  };

  return (
    <GlassCard
      className="p-6 mt-4"
      delay={0.08}
      data-testid="content-restrictions-card"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-9 w-9 rounded-md bg-[color:rgba(255,176,32,0.10)] text-[color:var(--warning)] flex items-center justify-center shrink-0 mt-0.5">
          <ShieldAlert className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="eyebrow">Optional</div>
          <div className="font-display text-xl font-semibold text-[color:var(--text-1)]">
            Content Restrictions &amp; Review
          </div>
          <div className="text-xs text-[color:var(--text-3)] mt-1 max-w-2xl">
            Give Postmoro your own content restrictions and disclosure preferences.
            Postmoro will try to follow the instructions you provide when generating
            AI captions. Your organization remains responsible for reviewing content
            before publishing — Postmoro does not provide legal, regulatory, or
            compliance approval.
          </div>
        </div>
      </div>

      {/* Master toggle */}
      <div
        className="mt-5 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
        data-testid="content-restrictions-toggle-row"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <div className="text-sm font-medium text-[color:var(--text-1)]">
              Use my content restrictions during AI generation
            </div>
            <div className="text-xs text-[color:var(--text-3)] mt-0.5">
              When on, Postmoro layers your instructions into every AI caption prompt.
              Manual review is still strongly recommended.
            </div>
          </div>
          <Switch
            checked={!!state.enabled}
            disabled={!canEnable && !state.enabled}
            onCheckedChange={(v) => {
              if (v && !state.acknowledged) {
                toast.error(
                  "Please acknowledge the review responsibility statement before enabling content restrictions.",
                );
                return;
              }
              set({ enabled: !!v });
            }}
            data-testid="content-restrictions-enabled-switch"
          />
        </div>
        {!state.acknowledged ? (
          <div
            className="mt-2 text-xs text-[color:var(--warning)]"
            data-testid="content-restrictions-toggle-helper"
          >
            Acknowledge the statement below to enable content restrictions.
          </div>
        ) : null}
      </div>

      {/* Amber banner when enabled */}
      {bannerVisible ? (
        <div
          className="mt-4 p-3 rounded-md bg-[color:rgba(255,176,32,0.08)] border border-[color:rgba(255,176,32,0.35)] flex items-start gap-3"
          data-testid="content-restrictions-manual-review-banner"
        >
          <AlertTriangle className="h-4 w-4 text-[color:var(--warning)] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <div className="text-sm font-medium text-[color:var(--warning)]">
              Manual review strongly recommended
            </div>
            <div className="text-xs text-[color:var(--text-2)] mt-0.5">
              Postmoro will try to follow the content restrictions you provide, but
              AI-generated content may contain errors or miss requirements. Review
              each post before publishing.
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/autopilot")}
            className="shrink-0 border-[color:rgba(255,176,32,0.45)] text-[color:var(--warning)] hover:bg-[color:rgba(255,176,32,0.10)]"
            data-testid="content-restrictions-review-approval-btn"
          >
            Review approval settings
          </Button>
        </div>
      ) : null}

      {/* Text-area fields (always visible so users can prepare before enabling) */}
      <div className="mt-5 grid grid-cols-1 gap-4">
        <div>
          <label className="text-xs text-[color:var(--text-2)]">
            Industry or rules that apply
          </label>
          <Textarea
            value={state.industry_or_rules || ""}
            onChange={(e) => set({ industry_or_rules: e.target.value.slice(0, MAX_LEN) })}
            placeholder="Example: Financial services, insurance advertising rules, internal company marketing policies"
            data-testid="content-restrictions-industry"
            className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] min-h-[80px]"
            maxLength={MAX_LEN}
          />
          <div className="text-[11px] text-[color:var(--text-3)] mt-1 text-right">
            {characterCount(state.industry_or_rules)} / {MAX_LEN}
          </div>
        </div>

        <div>
          <label className="text-xs text-[color:var(--text-2)]">
            Claims or language Postmoro should avoid
          </label>
          <Textarea
            value={state.prohibited_claims || ""}
            onChange={(e) => set({ prohibited_claims: e.target.value.slice(0, MAX_LEN) })}
            placeholder="Example: Do not promise guaranteed returns. Do not claim that results are risk-free."
            data-testid="content-restrictions-prohibited"
            className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] min-h-[80px]"
            maxLength={MAX_LEN}
          />
          <div className="text-[11px] text-[color:var(--text-3)] mt-1 text-right">
            {characterCount(state.prohibited_claims)} / {MAX_LEN}
          </div>
        </div>

        <div>
          <label className="text-xs text-[color:var(--text-2)]">
            Disclosures Postmoro should include
          </label>
          <Textarea
            value={state.required_disclosures || ""}
            onChange={(e) => set({ required_disclosures: e.target.value.slice(0, MAX_LEN) })}
            placeholder="Example: Investing involves risk, including possible loss of principal."
            data-testid="content-restrictions-disclosures"
            className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] min-h-[80px]"
            maxLength={MAX_LEN}
          />
          <div className="text-[11px] text-[color:var(--text-3)] mt-1 text-right">
            {characterCount(state.required_disclosures)} / {MAX_LEN}
          </div>
        </div>

        <div>
          <label className="text-xs text-[color:var(--text-2)]">
            Additional content restrictions
          </label>
          <Textarea
            value={state.additional_restrictions || ""}
            onChange={(e) => set({ additional_restrictions: e.target.value.slice(0, MAX_LEN) })}
            placeholder="Add any other topics, phrases, offers, hashtags, or statements that should be avoided."
            data-testid="content-restrictions-additional"
            className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] min-h-[80px]"
            maxLength={MAX_LEN}
          />
          <div className="text-[11px] text-[color:var(--text-3)] mt-1 text-right">
            {characterCount(state.additional_restrictions)} / {MAX_LEN}
          </div>
        </div>
      </div>

      {/* Acknowledgement */}
      <div
        className="mt-5 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
        data-testid="content-restrictions-acknowledge-row"
      >
        <div
          className="text-xs font-semibold text-[color:var(--text-1)] mb-2"
          data-testid="content-restrictions-ack-title"
        >
          Required to enable content restrictions
        </div>
        <div className="flex items-start gap-3">
          <Checkbox
            id="content-restrictions-ack"
            checked={!!state.acknowledged}
            onCheckedChange={(v) => {
              const next = !!v;
              // If un-checking while enabled, disable too (matches server enforcement).
              if (!next && state.enabled) {
                set({ acknowledged: false, enabled: false });
              } else {
                set({ acknowledged: next });
              }
            }}
            data-testid="content-restrictions-ack-checkbox"
            className="mt-0.5"
          />
          <label
            htmlFor="content-restrictions-ack"
            className="text-xs text-[color:var(--text-2)] leading-relaxed cursor-pointer"
          >
            I understand that Postmoro uses these instructions as
            content-generation and compliance-checking preferences and does not
            provide legal, regulatory, or compliance approval. My organization
            is responsible for reviewing content before publishing.
          </label>
        </div>
      </div>

      {/* Save controls */}
      <div className="mt-5 flex items-center justify-end gap-2 flex-wrap">
        {dirty ? (
          <Button
            variant="ghost"
            onClick={onReset}
            disabled={saving}
            data-testid="content-restrictions-reset-btn"
            className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
          >
            Reset
          </Button>
        ) : null}
        <Button
          onClick={onSave}
          disabled={saving || !dirty}
          className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
          data-testid="content-restrictions-save-btn"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          Save restrictions
        </Button>
      </div>

      {/* Phase 22: Compliance Policy Library — supplements the manual
          fields above. Never overwrites them; approvals here become
          part of the same effective Gate C policy the manual fields
          already feed. */}
      <CompliancePolicyLibrary effectiveCr={state} />
    </GlassCard>
  );
};

export default ContentRestrictionsSection;
